import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ModuleHeader } from 'components/modals';
import type { ActionButton, ViewModeOption } from 'components/modals';
import { filterTicketsByTab } from './ticketUtils';
import { SkeletonStyles, StatsSkeleton, TableSkeleton, GridViewSkeleton, LoadingOverlay } from './DashboardSkeleton';
import NewTicketModal from './NewTicketModal';
import TakeActionModal from './TakeActionModal';
import TicketModalRouter from './ticket-details/TicketModalRouter';
import ResubmitRequestModal from './ticket-details/ResubmitRequestModal';
import CancelRequestModal from './ticket-details/CancelRequestModal';
import ConsolidatedRequestModal from './ConsolidatedRequestModal';
import type { PreApprovedTicket, ConsolidatedRequestFormData } from './ConsolidatedRequestModal';
import { useTakeAction, useRequests, useDashboardSummary, useMyWorkSummary, useActiveWorkflowTemplates, useRequestDetails, useAuth, useStatusSummary, useTypeSummary } from 'hooks/api';
import { useConsolidationStatistics, useConsolidationActions } from 'hooks/api/das/useConsolidation';
import { ConsolidationStatisticsCard } from './consolidation';
import type { CreateConsolidatedRequestRequest } from 'api/types/das/consolidation.types';
import { attachmentsService } from 'api/services/das/attachmentsService';
import { mapRequestDetailsToTicket } from 'api/mappers/requestToTicketMapper';
import type {
  TabFilterType,
  RequestType,
  TicketPriority,
  TicketStage,
  TicketRole,
  ActionStatus,
  DASTicketListItem,
  ID,
  Request,
} from 'api/types';

/**
 * ============================================================================
 * DIGITAL APPROVAL SYSTEM - UNIFIED DASHBOARD v2.0
 * ============================================================================
 *
 * Features:
 * ✅ Tabbed navigation with 7 views
 * ✅ Comprehensive ticket listing with all required columns
 * ✅ Column visibility control (Manage Columns)
 * ✅ Grid view with ticket cards
 * ✅ Color-coded status indicators
 * ✅ Sorting and filtering capabilities
 * ✅ Search functionality
 * ✅ Responsive design with glassmorphism
 * ============================================================================
 */
// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface TabConfig {
  id: TabFilterType;
  label: string;
  badge?: number;
}

interface ColumnDefinition {
  id: string;
  label: string;
  field: string;
  required: boolean;
  defaultVisible: boolean;
  sortable: boolean;
  width?: number;
}

type ViewMode = 'list' | 'grid' | 'calendar';

// ============================================================================
// CONFIGURATION
// ============================================================================

const TAB_CONFIGS: TabConfig[] = [
  { id: 'my-active-items', label: 'My Worklist' },
  { id: 'requests-created-by-me', label: 'My Requests' },
  { id: 'my-completed-actions', label: 'My Completed Actions' },
  { id: 'pre-approved-items', label: 'Pre-Approved Requests' },
  { id: 'all-tickets', label: 'All Requests' },
];

const COLUMN_DEFINITIONS: ColumnDefinition[] = [
  {
    id: 'ticketNo',
    label: 'Request No',
    field: 'ticketNo',
    required: true,
    defaultVisible: true,
    sortable: true,
    width: 140,
  },
  {
    id: 'subject',
    label: 'Subject',
    field: 'subject',
    required: true,
    defaultVisible: true,
    sortable: true,
  },
  {
    id: 'requestType',
    label: 'Request Type',
    field: 'requestType',
    required: true,
    defaultVisible: true,
    sortable: true,
    width: 150,
  },
  {
    id: 'yourRole',
    label: 'Your Role',
    field: 'yourRole',
    required: true,
    defaultVisible: true,
    sortable: true,
    width: 180,
  },
  {
    id: 'currentStage',
    label: 'Current Stage',
    field: 'currentStage',
    required: true,
    defaultVisible: true,
    sortable: true,
    width: 200,
  },
  {
    id: 'yourActionStatus',
    label: 'Your Action Status',
    field: 'yourActionStatus',
    required: true,
    defaultVisible: true,
    sortable: true,
    width: 180,
  },
  {
    id: 'priority',
    label: 'Priority',
    field: 'priority',
    required: true,
    defaultVisible: true,
    sortable: true,
    width: 100,
  },
  {
    id: 'pendingSince',
    label: 'Pending Since (Age)',
    field: 'pendingSince',
    required: true,
    defaultVisible: true,
    sortable: true,
    width: 150,
  },
  {
    id: 'comments',
    label: 'Comments',
    field: 'comments',
    required: false,
    defaultVisible: false,
    sortable: false,
    width: 200,
  },
  {
    id: 'documentCount',
    label: 'No. of Documents',
    field: 'documentCount',
    required: false,
    defaultVisible: false,
    sortable: true,
    width: 120,
  },
  {
    id: 'finalApprover',
    label: 'Final Approver',
    field: 'finalApproverName',
    required: false,
    defaultVisible: false,
    sortable: true,
    width: 150,
  },
  {
    id: 'actions',
    label: 'Actions',
    field: 'actions',
    required: true,
    defaultVisible: true,
    sortable: false,
    width: 80,
  },
];

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

const getPriorityColor = (priority: TicketPriority): string => {
  switch (priority) {
    case 'Urgent':
      return 'badge-danger';
    case 'High':
      return 'badge-warning';
    case 'Medium':
      return 'badge-info';
    case 'Normal':
      return 'badge-secondary';
    default:
      return 'badge-secondary';
  }
};

/**
 * Returns hex color for priority styling (for inline styles)
 */
const getPriorityHexColor = (priority: TicketPriority): string => {
  switch (priority) {
    case 'Urgent':
      return '#dc2626'; // red
    case 'High':
      return '#f59e0b'; // amber/orange
    case 'Medium':
      return '#3b82f6'; // blue
    case 'Normal':
      return '#64748b'; // slate/gray
    default:
      return '#64748b';
  }
};

/**
 * Returns hex color for stage/status styling (for inline styles)
 */
const getStageHexColor = (stage: TicketStage): string => {
  if (stage === 'Approved') return '#10b981'; // green
  if (stage === 'Pre Approved') return '#06b6d4'; // cyan
  if (stage === 'Rejected') return '#ef4444'; // red
  if (stage === 'Returned') return '#f59e0b'; // amber
  if (stage === 'Cancelled') return '#6b7280'; // gray
  if (stage === 'Draft') return '#9ca3af'; // light gray
  if (stage.includes('Escalated')) return '#f97316'; // orange
  if (stage.includes('Sent Back')) return '#8b5cf6'; // purple
  if (stage.includes('In Review') || stage.includes('Submitted')) return '#3b82f6'; // blue
  return '#3b82f6'; // default blue
};

const getActionStatusColor = (status: string): string => {
  if (status === 'Pending – Your action needed') return 'badge-warning';
  if (status === 'Pending – Awaiting previous approver') return 'badge-info';
  if (status === 'Approved by You' || status.includes('Approved')) return 'badge-success';
  if (status === 'Rejected by You' || status.includes('Rejected')) return 'badge-danger';
  if (status === 'Returned by You' || status.includes('Returned')) return 'badge-warning';
  if (status.includes('Pre Approved')) return 'badge-info';
  if (status.includes('Recommended')) return 'badge-success';
  return 'badge-secondary';
};

/**
 * Formats the user's action status from the API to a user-friendly display format
 * @param userActionStatus - Raw status from tbl_request_approvers.approval_status
 * @returns Formatted display string like "Approved by You", "Rejected by You", etc.
 */
const formatUserActionStatus = (userActionStatus?: string): ActionStatus => {
  if (!userActionStatus) return 'No Action Required';

  const status = userActionStatus.toLowerCase();
  if (status === 'approved') return 'Approved by You';
  if (status === 'rejected') return 'Rejected by You';
  if (status === 'returned' || status === 'request changes') return 'Returned by You';
  if (status === 'pre approved') return 'Pre Approved by You';
  if (status === 'pending') return 'Pending – Your action needed';

  // Return as-is with " by You" suffix if not recognized
  return `${userActionStatus} by You` as ActionStatus;
};

const getStageColor = (stage: TicketStage): string => {
  if (stage === 'Approved') return 'badge-success';
  if (stage === 'Pre Approved') return 'badge-info';
  if (stage === 'Rejected') return 'badge-danger';
  if (stage === 'Returned') return 'badge-warning';
  if (stage.includes('Escalated')) return 'badge-warning';
  if (stage.includes('Sent Back')) return 'badge-info';
  return 'badge-primary';
};

/**
 * Maps API Request data to DASTicketListItem format for UI display
 */
const mapRequestToTicketListItem = (request: Request, userRole: TicketRole = 'Creator', actionStatus: ActionStatus = 'No Action Required'): DASTicketListItem => {
  // Calculate pending age (days since submission or creation)
  // Use submittedAt if available, otherwise fall back to createdAt
  const dateStr = request.submittedAt || request.createdAt;
  const referenceDate = dateStr ? new Date(dateStr) : new Date();
  const today = new Date();
  const pendingAgeDays = Math.max(0, Math.ceil((today.getTime() - referenceDate.getTime()) / (1000 * 60 * 60 * 24)));

  // Format date for display (handle invalid dates)
  const formattedDate = referenceDate instanceof Date && !isNaN(referenceDate.getTime())
    ? referenceDate.toISOString().split('T')[0]
    : new Date().toISOString().split('T')[0];

  // Map priority - backend values (Normal, Medium, High, Urgent) match TicketPriority directly
  const mappedPriority = (request.priority || 'Normal') as TicketPriority;

  // Map status to stage - prefer currentWorkflowStep from API if available
  const stageMap: Record<string, TicketStage> = {
    'Draft': 'Draft',
    'Submitted': 'Submitted',
    'In Review': 'In Review',
    'Pre Approved': 'Pre Approved',
    'Approved': 'Approved',
    'Rejected': 'Rejected',
    'Returned': 'Returned',
    'On Hold': 'Sent Back to Creator',
    'Cancelled': 'Cancelled'
  };
  // Use currentWorkflowStep if available, otherwise fall back to status mapping
  const mappedStage = request.currentWorkflowStep
    ? (request.currentWorkflowStep as TicketStage)
    : (stageMap[request.currentStatus] || request.currentStatus as TicketStage);

  // Map request type name to RequestType (from backend, with fallback)
  // RequestType is a flexible string type, so we use the backend value directly
  const mappedRequestType: RequestType = request.requestTypeName || 'General Request';

  return {
    id: request.id,
    ticketNo: request.requestNumber,
    subject: request.title,
    requestType: mappedRequestType,
    yourRole: userRole,
    currentStage: mappedStage,
    yourActionStatus: actionStatus,
    priority: mappedPriority,
    pendingSince: formattedDate,
    pendingAge: pendingAgeDays,
    comments: request.description,
    documentCount: request.attachmentCount || 0, // Attachment count from backend
    value: request.requestValue, // Monetary value from backend
    lastActionBy: request.preApprovedByName, // Pre-approved by user name
    finalApproverUserId: request.finalApproverUserId?.toString(),
    finalApproverName: request.finalApproverName,
    createdByUserName: request.submittedByName, // Creator's name from backend
    returnedToUserId: request.returnedToUserId?.toString(), // User ID the request was returned to
  };
};

// ============================================================================
// COLUMN SELECTOR COMPONENT
// ============================================================================

interface ColumnSelectorProps {
  columns: ColumnDefinition[];
  visibleColumns: string[];
  onApply: (visibleColumns: string[]) => void;
  onReset: () => void;
}

const ColumnSelector: React.FC<ColumnSelectorProps> = ({
  columns,
  visibleColumns: initialVisible,
  onApply,
  onReset,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState<string[]>(initialVisible);
  const [isMobile, setIsMobile] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSelectedColumns(initialVisible);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, initialVisible]);

  const toggleColumn = (columnId: string) => {
    const column = columns.find((col) => col.id === columnId);
    if (column?.required) return;

    setSelectedColumns((prev) =>
      prev.includes(columnId) ? prev.filter((id) => id !== columnId) : [...prev, columnId]
    );
  };

  const handleSelectAll = () => {
    const allColumnIds = columns.filter((col) => !col.required).map((col) => col.id);

    const requiredIds = columns.filter((col) => col.required).map((col) => col.id);

    if (selectedColumns.length === columns.length) {
      setSelectedColumns(requiredIds);
    } else {
      setSelectedColumns([...requiredIds, ...allColumnIds]);
    }
  };

  const handleReset = () => {
    if (window.confirm('Reset to default column configuration?')) {
      onReset();
      setIsOpen(false);
    }
  };

  const handleApply = () => {
    onApply(selectedColumns);
    setIsOpen(false);
  };

  const handleCancel = () => {
    setSelectedColumns(initialVisible);
    setIsOpen(false);
  };

  return (
    <div style={{ position: 'relative', display: 'inline-block' }} ref={panelRef}>
      <button
        className={`btn-icon ${isOpen ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        title="Manage Columns"
        style={
          isOpen
            ? {
                background: 'var(--accent-gradient)',
                color: 'white',
                borderColor: 'var(--primary)',
              }
            : {}
        }
      >
        <IconSystem.grid size={18} />
      </button>

      {isOpen && (
        <>
          {/* Backdrop for mobile */}
          {isMobile && (
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.5)',
                zIndex: 999,
              }}
              onClick={() => setIsOpen(false)}
            />
          )}

          <div
            style={{
              position: isMobile ? 'fixed' : 'absolute',
              top: isMobile ? '10%' : 'calc(100% + 0.5rem)',
              left: isMobile ? '50%' : 'auto',
              right: isMobile ? 'auto' : 0,
              transform: isMobile ? 'translateX(-50%)' : 'none',
              width: isMobile ? 'calc(100vw - 2rem)' : '320px',
              maxWidth: '320px',
              maxHeight: isMobile ? 'calc(90vh - 2rem)' : '500px',
              background: 'var(--bg-primary)',
              border: '2px solid var(--primary)',
              borderRadius: '0.75rem',
              boxShadow: '0 12px 32px rgba(0, 0, 0, 0.15)',
              zIndex: 1000,
              animation: 'slideDown 0.2s ease',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {/* Header */}
            <div
              style={{
                padding: '1rem 1.25rem',
                borderBottom: '2px solid var(--border-color)',
                background: 'var(--bg-secondary)',
                borderRadius: '0.75rem 0.75rem 0 0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexShrink: 0,
              }}
            >
              <h3
                style={{
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  color: 'var(--text-primary)',
                  margin: 0,
                }}
              >
                Manage Columns
              </h3>
              <button
                onClick={() => setIsOpen(false)}
                style={{
                  width: '1.5rem',
                  height: '1.5rem',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '0.25rem',
                }}
              >
                <IconSystem.x size={16} />
              </button>
            </div>

            {/* Content */}
            <div
              style={{
                flex: 1,
                overflowY: 'auto',
                padding: '0.75rem 0',
                minHeight: 0,
              }}
            >
              {columns.map((column) => (
                <div
                  key={column.id}
                  onClick={() => toggleColumn(column.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0.625rem 1.25rem',
                    cursor: column.required ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                    gap: '0.75rem',
                    opacity: column.required ? 0.6 : 1,
                  }}
                  onMouseEnter={(e) => {
                    if (!column.required) {
                      e.currentTarget.style.background = '#f8fafc';
                      e.currentTarget.style.paddingLeft = '1.5rem';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.paddingLeft = '1.25rem';
                  }}
                >
                  <div
                    style={{
                      width: '1.125rem',
                      height: '1.125rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px solid var(--border-color)',
                      borderRadius: '0.25rem',
                      transition: 'all 0.2s ease',
                      flexShrink: 0,
                      ...(selectedColumns.includes(column.id)
                        ? {
                            background: 'var(--accent-gradient)',
                            borderColor: 'var(--primary)',
                            color: 'white',
                          }
                        : {}),
                    }}
                  >
                    {selectedColumns.includes(column.id) && <IconSystem.check size={14} />}
                  </div>
                  <span
                    style={{
                      flex: 1,
                      fontSize: '0.85rem',
                      color: 'var(--text-primary)',
                      userSelect: 'none',
                    }}
                  >
                    {column.label}
                    {column.required && ' *'}
                  </span>
                </div>
              ))}
            </div>

            {/* Footer */}
            <div
              style={{
                padding: '1rem',
                borderTop: '2px solid var(--border-color)',
                background: 'var(--bg-primary)',
                boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.05)',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem',
                flexShrink: 0,
              }}
            >
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  onClick={handleSelectAll}
                  style={{
                    flex: 1,
                    padding: '0.5rem 0.75rem',
                    fontSize: '0.75rem',
                    background: 'var(--bg-primary)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '0.5rem',
                    cursor: 'pointer',
                  }}
                >
                  {selectedColumns.length === columns.length ? 'Deselect All' : 'Select All'}
                </button>
                <button
                  onClick={handleReset}
                  style={{
                    flex: 1,
                    padding: '0.5rem 0.75rem',
                    fontSize: '0.75rem',
                    background: 'var(--bg-primary)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '0.5rem',
                    cursor: 'pointer',
                  }}
                >
                  Reset Default
                </button>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                <button onClick={handleCancel} className="btn btn-secondary">
                  Cancel
                </button>
                <button onClick={handleApply} className="btn btn-primary">
                  Apply
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ============================================================================
// TICKET CARD COMPONENT (Grid View)
// ============================================================================

interface TicketCardProps {
  ticket: DASTicketListItem;
  onView: (id: string) => void;
  onTakeAction: (id: string) => void;
}

const TicketCard: React.FC<TicketCardProps> = ({ ticket, onView, onTakeAction }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };

    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpen]);

  return (
    <div
      style={{
        background: 'var(--bg-primary)',
        border: '1px solid var(--border-color)',
        borderRadius: '0.75rem',
        padding: '1.25rem',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        transition: 'all 0.2s ease',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(37, 99, 235, 0.15)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)';
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontSize: '0.75rem',
              fontWeight: 600,
              color: 'var(--text-secondary)',
              marginBottom: '0.25rem',
            }}
          >
            {ticket.ticketNo}
          </div>
          <h3
            style={{
              fontSize: '1rem',
              fontWeight: 700,
              color: 'var(--text-primary)',
              margin: 0,
              marginBottom: '0.5rem',
            }}
          >
            {ticket.subject}
          </h3>
        </div>
        <div ref={menuRef} style={{ position: 'relative' }}>
          <button
            className={`action-menu-trigger ${menuOpen ? 'active' : ''}`}
            onClick={() => setMenuOpen(!menuOpen)}
            style={{ marginTop: '-0.25rem' }}
          >
            <IconSystem.moreVertical size={16} />
          </button>
          {menuOpen && (
            <div className="action-menu-dropdown" style={{ right: 0, top: 'calc(100% + 0.5rem)' }}>
              <button
                className="action-menu-item"
                onClick={() => {
                  onView(ticket.id as string);
                  setMenuOpen(false);
                }}
              >
                <IconSystem.eye size={18} />
                <span>View Details</span>
              </button>
              {ticket.yourActionStatus === 'Pending – Your action needed' && (
                <button
                  className="action-menu-item"
                  onClick={() => {
                    onTakeAction(ticket.id as string);
                    setMenuOpen(false);
                  }}
                >
                  <IconSystem.check size={18} />
                  <span>Take Action</span>
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Request Type */}
      {ticket.requestType && (
        <div
          style={{
            fontSize: '0.85rem',
            color: 'var(--text-secondary)',
            fontWeight: 500,
          }}
        >
          {ticket.requestType}
        </div>
      )}

      {/* Details Grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '0.75rem',
          paddingTop: '0.75rem',
          borderTop: '1px solid rgba(226, 232, 240, 0.5)',
        }}
      >
        <div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>
            Your Role
          </div>
          <div style={{ fontSize: '0.85rem', fontWeight: 500, color: '#1e293b' }}>
            {ticket.yourRole}
          </div>
        </div>
        <div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>
            Priority
          </div>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '0.25rem 0.5rem',
              fontSize: '0.75rem',
              fontWeight: 600,
              borderRadius: '0.25rem',
              background: `${getPriorityHexColor(ticket.priority)}15`,
              color: getPriorityHexColor(ticket.priority),
            }}
          >
            {ticket.priority}
          </span>
        </div>
        <div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>
            Current Stage
          </div>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '0.25rem 0.5rem',
              fontSize: '0.75rem',
              fontWeight: 600,
              borderRadius: '0.25rem',
              background: `${getStageHexColor(ticket.currentStage)}15`,
              color: getStageHexColor(ticket.currentStage),
            }}
          >
            {ticket.currentStage}
          </span>
        </div>
        <div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>
            Your Action
          </div>
          <span className={`badge ${getActionStatusColor(ticket.yourActionStatus)}`}>
            {ticket.yourActionStatus}
          </span>
        </div>
      </div>

      {/* Footer */}
      <div
        style={{
          paddingTop: '0.75rem',
          borderTop: '1px solid rgba(226, 232, 240, 0.5)',
          fontSize: '0.75rem',
          color: '#64748b',
        }}
      >
        <strong>Pending Since:</strong> {ticket.pendingSince} ({ticket.pendingAge} days)
      </div>
    </div>
  );
};

// ============================================================================
// ACTION MENU COMPONENT
// ============================================================================

interface ActionMenuProps {
  rowId: string;
  onView: (id: string) => void;
  onTakeAction: (id: string) => void;
  onMenuStateChange: (rowId: string, isOpen: boolean) => void;
  yourActionStatus?: ActionStatus;
  currentStage?: string;
  yourRole?: TicketRole;
  onResubmit?: (id: string) => void;
  onCancel?: (id: string) => void;
  returnedToUserId?: string; // User ID the request was returned to
  currentUserId?: string; // Currently logged in user's ID
}

const ActionMenu: React.FC<ActionMenuProps> = ({
  rowId,
  onView,
  onTakeAction,
  onMenuStateChange,
  yourActionStatus,
  currentStage,
  yourRole,
  onResubmit,
  onCancel,
  returnedToUserId,
  currentUserId,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  React.useEffect(() => {
    onMenuStateChange(rowId, isOpen);
  }, [isOpen, rowId, onMenuStateChange]);

  return (
    <div className="action-menu-container" ref={menuRef}>
      <button
        className={`action-menu-trigger ${isOpen ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <IconSystem.moreVertical size={16} />
      </button>

      {isOpen && (
        <div className="action-menu-dropdown">
          <button
            className="action-menu-item"
            onClick={() => {
              onView(rowId);
              setIsOpen(false);
            }}
          >
            <IconSystem.eye size={18} />
            <span>View Details</span>
          </button>
          {/* Take Action - show only when action needed AND Resubmit doesn't apply */}
          {(() => {
            const isReturnedOrSentBack = currentStage === 'Returned' || currentStage === 'Sent Back to Creator';
            const isReturnedToCurrentUser = returnedToUserId && currentUserId && returnedToUserId === currentUserId;
            const isLegacyCreatorReturn = !returnedToUserId && yourRole === 'Creator';
            const shouldShowResubmit = isReturnedOrSentBack && (isReturnedToCurrentUser || isLegacyCreatorReturn);
            return yourActionStatus === 'Pending – Your action needed' && !shouldShowResubmit;
          })() && (
            <button
              className="action-menu-item"
              onClick={() => {
                onTakeAction(rowId);
                setIsOpen(false);
              }}
            >
              <IconSystem.check size={18} />
              <span>Take Action</span>
            </button>
          )}
          {/* Resubmit option - for users who need to respond to returned requests */}
          {/* Show when: (1) Stage is Returned/Sent Back AND (2) returnedToUserId matches current user OR (legacy) no returnedToUserId and user is Creator */}
          {(() => {
            const isReturnedOrSentBack = currentStage === 'Returned' || currentStage === 'Sent Back to Creator';
            const isReturnedToCurrentUser = returnedToUserId && currentUserId && returnedToUserId === currentUserId;
            const isLegacyCreatorReturn = !returnedToUserId && yourRole === 'Creator';
            return isReturnedOrSentBack && (isReturnedToCurrentUser || isLegacyCreatorReturn) && onResubmit;
          })() && (
            <button
              className="action-menu-item"
              onClick={() => {
                onResubmit!(rowId);
                setIsOpen(false);
              }}
            >
              <IconSystem.refresh size={18} />
              <span>Resubmit</span>
            </button>
          )}
          {/* Cancel option - ONLY for creators with returned requests */}
          {/* Only the original creator can cancel their request, regardless of who it was returned to */}
          {yourRole === 'Creator' && (currentStage === 'Returned' || currentStage === 'Sent Back to Creator') && onCancel && (
            <button
              className="action-menu-item"
              style={{ color: '#dc2626' }}
              onClick={() => {
                onCancel(rowId);
                setIsOpen(false);
              }}
            >
              <IconSystem.x size={18} />
              <span>Cancel Request</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// MAIN DASHBOARD COMPONENT
// ============================================================================

const DashboardScreen: React.FC = () => {
  // Dark mode detection
  const [isDarkMode, setIsDarkMode] = useState(() => {
    return document.documentElement.getAttribute('data-theme') === 'dark';
  });

  // Listen for theme changes
  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'data-theme') {
          setIsDarkMode(document.documentElement.getAttribute('data-theme') === 'dark');
        }
      });
    });
    observer.observe(document.documentElement, { attributes: true });
    return () => observer.disconnect();
  }, []);

  // View mode state
  const [viewMode, setViewMode] = useState<ViewMode>('list');

  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() => {
    const defaultVisible = COLUMN_DEFINITIONS.filter((col) => col.defaultVisible).map(
      (col) => col.id
    );
    return defaultVisible;
  });

  // State
  const [activeTab, setActiveTab] = useState<TabFilterType>('my-active-items');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [activeMenuRowId, setActiveMenuRowId] = useState<string | null>(null);
  const [tabOverflowOpen, setTabOverflowOpen] = useState(false);
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState<ID | null>(null);
  const [showTakeActionModal, setShowTakeActionModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showResubmitModal, setShowResubmitModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  // Pre-approved items consolidation state
  const [selectedPreApprovedIds, setSelectedPreApprovedIds] = useState<string[]>([]);
  const [showConsolidateModal, setShowConsolidateModal] = useState(false);
  const [consolidationTitle, setConsolidationTitle] = useState('');
  const [consolidationDescription, setConsolidationDescription] = useState('');
  const [modalSelectedIds, setModalSelectedIds] = useState<string[]>([]); // Selected items within the modal
  // Enhanced attachments with title and description
  const [consolidationAttachments, setConsolidationAttachments] = useState<{ file: File; title: string; description: string }[]>([]);
  const [newAttachment, setNewAttachment] = useState<{ file: File | null; title: string; description: string }>({
    file: null,
    title: '',
    description: '',
  });
  // Preview attachment state for in-modal preview
  const [previewAttachment, setPreviewAttachment] = useState<{ url: string; fileName: string; fileType: string } | null>(null);

  // New ConsolidatedRequestModal state
  const [showNewConsolidateModal, setShowNewConsolidateModal] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Auth hook - get current user for role/action status calculation
  const { user: authUser } = useAuth();
  const currentUserId = authUser?.id;

  // Take Action hook
  const {
    ticket,
    approvalPosition,
    relatedTickets,
    approvalLevels,
    availableUsers,
    isLoading: takeActionLoading,
    error: takeActionError,
    takeAction,
  } = useTakeAction(selectedTicketId);

  // Request Details hook - for complete ticket details with approvers, history, attachments
  const {
    data: requestDetails,
    loading: requestDetailsLoading,
    error: requestDetailsError,
    refetch: refetchRequestDetails,
  } = useRequestDetails(selectedTicketId);

  // Transform request details to ticket format for UI display
  // Pass currentUserId to properly calculate yourRole and yourActionStatus
  const ticketDetails = requestDetails ? mapRequestDetailsToTicket(requestDetails, currentUserId) : null;

  // Memoize pagination params to avoid infinite re-renders
  const paginationParams = useMemo(
    () => ({ page: currentPage, pageSize }),
    [currentPage, pageSize]
  );

  // Requests data hook - provides paginated data for "All Requests" tab + pagination info
  const {
    items: requestItems,
    pagination: paginationData,
    loading: requestsLoading,
  } = useRequests(paginationParams);

  // Dashboard data hooks
  const { summary: dashboardSummary, loading: summaryLoading, error: summaryError, refetch: refetchSummary } = useDashboardSummary();
  const { workSummary: myWorkSummary, loading: workSummaryLoading, error: workSummaryError, refetch: refetchWorkSummary } = useMyWorkSummary();

  // Workflow templates hook
  const { templates: workflowTemplates } = useActiveWorkflowTemplates();

  // Filter options hooks - fetch from backend (uses data that actually exists in the system)
  const { typeSummary, error: typeSummaryError, refetch: refetchTypeSummary } = useTypeSummary(); // Request types that have actual requests
  const { statusSummary, error: statusSummaryError, refetch: refetchStatusSummary } = useStatusSummary(); // Statuses that have actual requests

  // Consolidation hooks - for two-level approval workflow
  const { data: consolidationStats, error: consolidationStatsError, refetch: refetchConsolidationStats } = useConsolidationStatistics();
  const { createConsolidation, loading: consolidationLoading, error: consolidationError } = useConsolidationActions();

  // Priority options - aligned with backend validation (Normal, Medium, High, Urgent)
  const PRIORITY_OPTIONS = ['Normal', 'Medium', 'High', 'Urgent'] as const;

  // Refs
  const tabOverflowRef = useRef<HTMLDivElement>(null);

  // Close overflow menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        tabOverflowRef.current &&
        !tabOverflowRef.current.contains(event.target as Node)
      ) {
        setTabOverflowOpen(false);
      }
    };

    if (tabOverflowOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [tabOverflowOpen]);

  // Load saved column preferences on mount
  useEffect(() => {
    const saved = localStorage.getItem('das-dashboard-columns');
    if (saved) {
      try {
        setVisibleColumns(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to load column preferences');
      }
    }
  }, []);

  // Filters
  const [requestTypeFilter, setRequestTypeFilter] = useState<RequestType | ''>('');
  const [priorityFilter, setPriorityFilter] = useState<TicketPriority | ''>('');
  const [stageFilter, setStageFilter] = useState<TicketStage | ''>('');

  // Dashboard Stats - Use ONLY real data from API (no mock data fallback)
  const stats = dashboardSummary ? {
    myActiveItems: dashboardSummary.requiresMyActionCount,
    myPendingApprovals: dashboardSummary.myPendingApprovalsCount,
    requestsCreatedByMe: dashboardSummary.myRequestsCount,
    // Use myWorkSummary counts for completed actions tabs
    completedInProgress: myWorkSummary?.myActionsInProgressCount ?? 0,
    completedClosed: myWorkSummary?.myActionsClosedCount ?? 0,
    escalatedToMe: 0, // TODO: Add to API response
    totalTickets: dashboardSummary.totalRequests,
    criticalTickets: 0, // TODO: Add to API response or calculate from requests
    highPriorityTickets: 0, // TODO: Add to API response or calculate from requests
  } : {
    myActiveItems: 0,
    myPendingApprovals: 0,
    requestsCreatedByMe: 0,
    completedInProgress: 0,
    completedClosed: 0,
    escalatedToMe: 0,
    totalTickets: 0,
    criticalTickets: 0,
    highPriorityTickets: 0,
  };

  // Map user-related requests to DASTicketListItem format for UI
  // Uses myWorkSummary data to show only requests relevant to the logged-in user
  const allTickets: DASTicketListItem[] = useMemo(() => {
    if (workSummaryLoading) {
      console.log('⏳ Work summary still loading...');
      return []; // Return empty array while loading
    }

    // Check if myWorkSummary exists
    if (!myWorkSummary) {
      console.warn('⚠️ myWorkSummary is null/undefined - API may have failed or returned no data');
      return [];
    }

    // Get requests from myWorkSummary (user-specific data) with defensive checks
    const pendingApprovals = Array.isArray(myWorkSummary.pendingApprovals) ? myWorkSummary.pendingApprovals : [];
    const myRequests = Array.isArray(myWorkSummary.myRequests) ? myWorkSummary.myRequests : [];
    const requiresAction = Array.isArray(myWorkSummary.requiresAction) ? myWorkSummary.requiresAction : [];
    const myActionsInProgress = Array.isArray(myWorkSummary.myActionsInProgress) ? myWorkSummary.myActionsInProgress : [];
    const myActionsClosed = Array.isArray(myWorkSummary.myActionsClosed) ? myWorkSummary.myActionsClosed : [];
    const preApprovedItems = Array.isArray(myWorkSummary.preApprovedItems) ? myWorkSummary.preApprovedItems : [];

    // Debug log the raw data
    console.log('🔍 Raw myWorkSummary data:', {
      myWorkSummaryExists: !!myWorkSummary,
      pendingApprovalsCount: pendingApprovals.length,
      myRequestsCount: myRequests.length,
      requiresActionCount: requiresAction.length,
      myActionsInProgressCount: myActionsInProgress.length,
      myActionsClosedCount: myActionsClosed.length,
      preApprovedItemsCount: preApprovedItems.length,
      rawMyWorkSummary: myWorkSummary,
    });

    // Use a Map to deduplicate requests (a request can appear in multiple categories)
    const ticketMap = new Map<string, DASTicketListItem>();

    // Helper function to safely map requests with error handling
    const safeMapRequest = (req: Request, role: TicketRole, actionStatus: ActionStatus): DASTicketListItem | null => {
      try {
        return mapRequestToTicketListItem(req, role, actionStatus);
      } catch (error) {
        console.error('❌ Error mapping request:', req.id, error);
        return null;
      }
    };

    // Pending approvals: user is Approver. Label depends on whether it's
    // actually the user's turn — under ShowWithRestriction the final approver
    // sees rows where level-1 is still pending, and for those rows the label
    // must say "Awaiting previous approver" (action button auto-hides because
    // it's gated on the 'Pending – Your action needed' label below).
    pendingApprovals.forEach(req => {
      const id = req.id as string;
      if (!ticketMap.has(id)) {
        const isReady = req.isReadyForMyAction !== false; // default true if undefined
        const label: ActionStatus = isReady
          ? 'Pending – Your action needed'
          : 'Pending – Awaiting previous approver';
        const mapped = safeMapRequest(req, 'Approver', label);
        if (mapped) ticketMap.set(id, mapped);
      }
    });

    // Requires action: user needs to take action (could be recommender, etc.)
    requiresAction.forEach(req => {
      const id = req.id as string;
      if (!ticketMap.has(id)) {
        const mapped = safeMapRequest(req, 'Recommender', 'Pending – Your action needed');
        if (mapped) ticketMap.set(id, mapped);
      }
    });

    // My requests: user is Creator
    myRequests.forEach(req => {
      const id = req.id as string;
      if (!ticketMap.has(id)) {
        // Determine action status based on request status
        const actionStatus: ActionStatus = req.currentStatus === 'Draft'
          ? 'Pending – Your action needed'
          : 'No Action Required';
        const mapped = safeMapRequest(req, 'Creator', actionStatus);
        if (mapped) ticketMap.set(id, mapped);
      }
    });

    // My actions in progress: user has approved/rejected but request is still in progress
    // IMPORTANT: Usually these take priority over pending approvals because the user HAS already taken action.
    // EXCEPTION: If the request was returned TO the current user, it should show as "requires action"
    // and NOT be overwritten by the previous action status.
    myActionsInProgress.forEach(req => {
      const id = req.id as string;
      // Check if this request was returned TO the current user
      // If so, show it as "requires action" - the user needs to resubmit
      const isReturnedToCurrentUser = req.currentStatus === 'Returned' &&
        req.returnedToUserId && currentUserId &&
        String(req.returnedToUserId) === String(currentUserId);

      if (isReturnedToCurrentUser) {
        // If request is returned TO the current user:
        // - If already in map (from requiresAction), don't overwrite
        // - If NOT in map yet, add it with "action needed" status so user can resubmit
        if (!ticketMap.has(id)) {
          // Request wasn't in requiresAction, but it's returned to this user - add it
          const mapped = safeMapRequest(req, 'Recommender', 'Pending – Your action needed');
          if (mapped) ticketMap.set(id, mapped);
        }
        return;
      }

      // Use userActionStatus from API to show actual action taken (Approved/Rejected/Pre Approved)
      const actionStatus = formatUserActionStatus(req.userActionStatus);
      const mapped = safeMapRequest(req, 'Approver', actionStatus);
      if (mapped) ticketMap.set(id, mapped);
    });

    // My actions closed: user has approved/rejected and request is now closed
    // IMPORTANT: Always set/update these - they take priority over other statuses
    myActionsClosed.forEach(req => {
      const id = req.id as string;
      // Always set (don't skip if exists) - user's completed action takes priority
      // Use userActionStatus from API to show actual action taken (Approved/Rejected)
      const actionStatus = formatUserActionStatus(req.userActionStatus);
      const mapped = safeMapRequest(req, 'Approver', actionStatus);
      if (mapped) ticketMap.set(id, mapped);
    });

    // Pre-approved items: user has pre-approved and items are awaiting consolidation
    // IMPORTANT: Only set for items that are NOT already in the map with role "Creator"
    // (consolidated requests created by the user should keep their "Creator" role)
    preApprovedItems.forEach(req => {
      const id = req.id as string;
      const existingTicket = ticketMap.get(id);
      // Only set as Pre-Approver if:
      // 1. Not already in the map, OR
      // 2. Existing entry is NOT a Creator role (don't overwrite creator's view)
      if (!existingTicket || existingTicket.yourRole !== 'Creator') {
        // Use userActionStatus from API or default to "Pre Approved by You"
        const actionStatus = formatUserActionStatus(req.userActionStatus || 'Pre Approved');
        const mapped = safeMapRequest(req, 'Pre-Approver', actionStatus);
        if (mapped) ticketMap.set(id, mapped);
      }
    });

    // NOTE: Removed the blanket override that changed ALL "Pre Approved" items to "Pre-Approver"
    // This was incorrect because consolidated requests created by the user should keep "Creator" role
    // The preApprovedItems list above correctly handles items where the user is the pre-approver

    // Convert Map to array and return
    const result = Array.from(ticketMap.values());
    console.log('✅ Mapped tickets:', result.length, 'items');
    return result;
  }, [myWorkSummary, workSummaryLoading]);

  // Map paginated request items from useRequests API for "All Requests" tab
  const allRequestTickets: DASTicketListItem[] = useMemo(() => {
    if (!requestItems || requestItems.length === 0) return [];
    return requestItems
      .map((req) => {
        try {
          return mapRequestToTicketListItem(req);
        } catch {
          return null;
        }
      })
      .filter((item): item is DASTicketListItem => item !== null);
  }, [requestItems]);

  // User-specific consolidation counts for status cards
  // These replace global consolidationStats in the dashboard view
  const userConsolidationCounts = useMemo(() => {
    // Pre-approved items: items where user acted as pre-approver, awaiting consolidation
    const preApprovedCount = myWorkSummary?.preApprovedItems?.length ?? 0;

    // Pending final approval: consolidated requests pending user's final approval
    // These are items in pendingApprovals that are consolidated requests (have finalApproverUserId)
    const pendingApprovals = myWorkSummary?.pendingApprovals ?? [];
    const pendingFinalApprovalCount = pendingApprovals.filter(
      req => req.finalApproverUserId && req.currentStatus === 'Submitted'
    ).length;

    return {
      preApprovedCount,
      pendingFinalApprovalCount,
      hasPreApprovedRole: preApprovedCount > 0,
      hasFinalApproverRole: pendingFinalApprovalCount > 0,
    };
  }, [myWorkSummary]);

  // Log API data for verification
  useEffect(() => {
    console.log('📊 Dashboard Data Status (User-Specific Data):');
    console.log('  - Summary loading:', summaryLoading);
    console.log('  - Summary available:', !!dashboardSummary);
    console.log('  - Summary error:', summaryError);

    console.log('  - Work Summary loading:', workSummaryLoading);
    console.log('  - Work Summary available:', !!myWorkSummary);
    console.log('  - Work Summary error:', workSummaryError);

    if (myWorkSummary) {
      console.log('  - Pending Approvals:', myWorkSummary.pendingApprovals?.length || 0);
      console.log('  - My Requests:', myWorkSummary.myRequests?.length || 0);
      console.log('  - Requires Action:', myWorkSummary.requiresAction?.length || 0);
      console.log('  - My Actions In Progress:', myWorkSummary.myActionsInProgress?.length || 0, `(count: ${myWorkSummary.myActionsInProgressCount || 0})`);
      console.log('  - My Actions Closed:', myWorkSummary.myActionsClosed?.length || 0, `(count: ${myWorkSummary.myActionsClosedCount || 0})`);
      console.log('  - Pre-Approved Items:', myWorkSummary.preApprovedItems?.length || 0, `(count: ${myWorkSummary.preApprovedItemsCount || 0})`);
    }

    // Log user-specific consolidation counts
    console.log('📊 User Consolidation Stats (for status cards):');
    console.log('  - User Pre-Approved Count:', userConsolidationCounts.preApprovedCount);
    console.log('  - User Pending Final Approval:', userConsolidationCounts.pendingFinalApprovalCount);
    console.log('  - Has Pre-Approver Role:', userConsolidationCounts.hasPreApprovedRole);
    console.log('  - Has Final Approver Role:', userConsolidationCounts.hasFinalApproverRole);

    console.log('  - All Tickets (user-related):', allTickets.length, 'items');

    if (allTickets.length > 0) {
      console.log('✅ First mapped ticket:', allTickets[0]);
    }

    if (summaryError || workSummaryError) {
      console.error('❌ API Errors detected - check backend connectivity');
      if (workSummaryError) {
        console.error('❌ Work Summary Error:', workSummaryError);
      }
    }
  }, [dashboardSummary, summaryError, summaryLoading, myWorkSummary, workSummaryError, workSummaryLoading, allTickets, userConsolidationCounts]);

  // Filtered data by tab
  // For "all-tickets" tab, use paginated data from useRequests API
  // For other tabs, use client-side filtered data from myWorkSummary
  const tabFilteredData = useMemo(() => {
    if (activeTab === 'all-tickets') {
      return allRequestTickets;
    }
    return filterTicketsByTab(allTickets, activeTab);
  }, [allTickets, allRequestTickets, activeTab]);

  // Apply search and filters
  const filteredData = useMemo(() => {
    return tabFilteredData.filter((ticket) => {
      const matchesSearch =
        ticket.ticketNo.toLowerCase().includes(searchQuery.toLowerCase()) ||
        ticket.subject.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesRequestType =
        requestTypeFilter === '' || ticket.requestType === requestTypeFilter;
      const matchesPriority = priorityFilter === '' || ticket.priority === priorityFilter;
      const matchesStage = stageFilter === '' || ticket.currentStage === stageFilter;

      return matchesSearch && matchesRequestType && matchesPriority && matchesStage;
    });
  }, [tabFilteredData, searchQuery, requestTypeFilter, priorityFilter, stageFilter]);

  // Visible column definitions
  // For pre-approved-items tab: include finalApprover column
  const visibleColumnDefs = useMemo(() => {
    const columnsToShow = activeTab === 'pre-approved-items'
      ? [...new Set([...visibleColumns, 'finalApprover'])]
      : visibleColumns;
    return COLUMN_DEFINITIONS.filter((col) => columnsToShow.includes(col.id));
  }, [visibleColumns, activeTab]);

  // Column visibility handlers
  const handleApplyColumns = useCallback((newVisibleColumns: string[]) => {
    setVisibleColumns(newVisibleColumns);
    localStorage.setItem('das-dashboard-columns', JSON.stringify(newVisibleColumns));
  }, []);

  const handleResetColumns = useCallback(() => {
    const defaultVisible = COLUMN_DEFINITIONS.filter((col) => col.defaultVisible).map(
      (col) => col.id
    );
    setVisibleColumns(defaultVisible);
    localStorage.removeItem('das-dashboard-columns');
  }, []);

  // Handlers
  const handleMenuStateChange = useCallback((rowId: string, isOpen: boolean) => {
    setActiveMenuRowId(isOpen ? rowId : null);
  }, []);

  const handleView = useCallback((id: string) => {
    setSelectedTicketId(id);
    setShowDetailsModal(true);
  }, []);

  const handleTakeAction = useCallback((id: string) => {
    setSelectedTicketId(id);
    setShowTakeActionModal(true);
  }, []);

  const handleResubmit = useCallback((id: string) => {
    setSelectedTicketId(id);
    setShowResubmitModal(true);
  }, []);

  const handleCancel = useCallback((id: string) => {
    setSelectedTicketId(id);
    setShowCancelModal(true);
  }, []);

  const handleRefresh = useCallback(() => {
    console.log('🔄 Refreshing dashboard data...');
    refetchSummary();
    refetchWorkSummary();
  }, [refetchSummary, refetchWorkSummary]);

  const handleExport = useCallback(() => {
    console.log('Export data');
    // TODO: Implement export logic
  }, []);

  const handleAddTicket = useCallback(() => {
    setNewTicketModalOpen(true);
  }, []);

  const handleClearFilters = useCallback(() => {
    setRequestTypeFilter('');
    setPriorityFilter('');
    setStageFilter('');
  }, []);

  // Tab change handler - reset pagination when switching tabs
  const handleTabChange = useCallback((tabId: TabFilterType) => {
    setActiveTab(tabId);
    setCurrentPage(1);
  }, []);

  // Pagination handlers
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleNextPage = useCallback(() => {
    if (paginationData?.hasNextPage) {
      setCurrentPage((prev) => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [paginationData?.hasNextPage]);

  const handlePreviousPage = useCallback(() => {
    if (paginationData?.hasPreviousPage) {
      setCurrentPage((prev) => prev - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [paginationData?.hasPreviousPage]);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when changing page size
  }, []);

  // Pre-approved items selection handlers
  const handleTogglePreApprovedSelection = useCallback((ticketId: string) => {
    setSelectedPreApprovedIds((prev) =>
      prev.includes(ticketId) ? prev.filter((id) => id !== ticketId) : [...prev, ticketId]
    );
  }, []);

  const handleSelectAllPreApproved = useCallback(() => {
    const preApprovedTickets = filteredData.filter((t) => t.currentStage === 'Pre Approved');
    if (selectedPreApprovedIds.length === preApprovedTickets.length) {
      setSelectedPreApprovedIds([]);
    } else {
      setSelectedPreApprovedIds(preApprovedTickets.map((t) => t.id as string));
    }
  }, [filteredData, selectedPreApprovedIds.length]);

  const handleOpenConsolidateModal = useCallback(() => {
    // Open the new ConsolidatedRequestModal directly (no selection required)
    const preApprovedCount = allTickets.filter((t) => t.currentStage === 'Pre Approved').length;
    console.log('📋 Opening Consolidation Modal:', {
      allTicketsCount: allTickets.length,
      preApprovedTicketsCount: preApprovedCount,
    });
    setShowNewConsolidateModal(true);
  }, [allTickets]);

  const handleCreateConsolidation = useCallback(async () => {
    if (modalSelectedIds.length === 0 || !consolidationTitle.trim()) {
      console.warn('Consolidation validation failed:', {
        selectedCount: modalSelectedIds.length,
        title: consolidationTitle
      });
      return;
    }

    // Get final approver from the first selected ticket
    const selectedTickets = modalSelectedIds
      .map((id) => filteredData.find((t) => t.id === id))
      .filter((t): t is DASTicketListItem => t !== undefined);
    const finalApproverUserId = selectedTickets.length > 0 ? selectedTickets[0].finalApproverUserId : undefined;

    const request: CreateConsolidatedRequestRequest = {
      title: consolidationTitle.trim(),
      description: consolidationDescription.trim() || undefined,
      preApprovedRequestIds: modalSelectedIds,
      finalApproverUserId: finalApproverUserId,
    };

    console.log('Creating consolidation with request:', request);

    try {
      const result = await createConsolidation(request);
      console.log('Consolidation result:', result);

      if (result) {
        setShowConsolidateModal(false);
        setSelectedPreApprovedIds([]);
        setModalSelectedIds([]);
        setConsolidationTitle('');
        setConsolidationDescription('');
        setConsolidationAttachments([]);
        setNewAttachment({ file: null, title: '', description: '' });
        // Refresh data
        refetchWorkSummary();
        refetchSummary();
      } else {
        // API call failed - error will be in consolidationError state
        console.error('Consolidation creation failed - check consolidationError state');
      }
    } catch (err) {
      console.error('Consolidation creation error:', err);
    }
  }, [modalSelectedIds, consolidationTitle, consolidationDescription, createConsolidation, refetchWorkSummary, refetchSummary, filteredData]);

  // Clear selection when switching tabs
  useEffect(() => {
    if (activeTab !== 'pre-approved-items') {
      setSelectedPreApprovedIds([]);
    }
  }, [activeTab]);

  // View mode options
  const viewModeOptions: ViewModeOption[] = [
    {
      id: 'list',
      icon: <IconSystem.list size={16} />,
      label: 'List View',
      tooltip: 'List View',
    },
    {
      id: 'grid',
      icon: <IconSystem.grid size={16} />,
      label: 'Grid View',
      tooltip: 'Grid View',
    },
    {
      id: 'calendar',
      icon: <IconSystem.calendar size={16} />,
      label: 'Calendar View',
      tooltip: 'Calendar View (Not Applicable)',
      disabled: true,
    },
  ];

  // Check if there are any Pre-Approved items in the current filtered data
  const hasPreApprovedItems = useMemo(() => {
    return filteredData.some((t) => t.currentStage === 'Pre Approved');
  }, [filteredData]);

  // Count of pre-approved items available for consolidation (need at least 2)
  const preApprovedItemsForConsolidation = useMemo(() => {
    return allTickets.filter((t) => t.currentStage === 'Pre Approved');
  }, [allTickets]);

  const canConsolidate = preApprovedItemsForConsolidation.length >= 2;

  // Action buttons (icon-only)
  const actionButtons: ActionButton[] = [
    // Consolidate button - only visible on pre-approved tab when there are items
    ...(activeTab === 'pre-approved-items' && hasPreApprovedItems
      ? [
          {
            id: 'consolidate',
            icon: <IconSystem.copy size={18} />,
            label: 'Consolidate',
            onClick: handleOpenConsolidateModal,
            tooltip: `Create Consolidated Request (${preApprovedItemsForConsolidation.length} items available)`,
            variant: 'icon' as const,
          },
        ]
      : []),
    {
      id: 'add',
      icon: <IconSystem.add size={18} />,
      label: 'Add',
      onClick: handleAddTicket,
      tooltip: 'Create New Request',
      variant: 'icon',
    },
    {
      id: 'refresh',
      icon: <IconSystem.refresh size={18} />,
      label: 'Refresh',
      onClick: handleRefresh,
      tooltip: 'Refresh',
      variant: 'icon',
    },
    {
      id: 'export',
      icon: <IconSystem.download size={18} />,
      label: 'Export',
      onClick: handleExport,
      tooltip: 'Export',
      variant: 'icon',
    },
  ];

  // Determine if we're in initial loading state (no data yet)
  // For "all-tickets" tab, also check useRequests loading state
  const isInitialLoading = activeTab === 'all-tickets'
    ? (summaryLoading || requestsLoading) && !dashboardSummary && !paginationData
    : (summaryLoading || workSummaryLoading) && !dashboardSummary && !myWorkSummary;
  // Determine if we're refreshing (have data but fetching new)
  const isRefreshing: boolean = Boolean((summaryLoading || workSummaryLoading) && (dashboardSummary || myWorkSummary));

  return (
    <div className="module-master-container">
      {/* Skeleton Styles for loading animations */}
      <SkeletonStyles />

      {/* Module Header */}
      <ModuleHeader
        visible={true}
        title="Digital Approval System"
        description="Unified dashboard for all approval workflows"
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search tickets...',
        }}
        viewModeConfig={{
          currentMode: viewMode,
          modes: viewModeOptions,
          onChange: (mode) => setViewMode(mode as ViewMode),
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen,
        }}
        customToolbarItems={
          <ColumnSelector
            columns={COLUMN_DEFINITIONS}
            visibleColumns={visibleColumns}
            onApply={handleApplyColumns}
            onReset={handleResetColumns}
          />
        }
        actions={actionButtons}
      />

      {/* Error Banner - Display API errors */}
      {(summaryError || workSummaryError || typeSummaryError || statusSummaryError || consolidationStatsError || consolidationError) && (
        <div
          className="error-banner"
          style={{
            margin: '0.5rem 1.5rem',
            padding: '0.75rem 1rem',
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '0.5rem',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '0.75rem',
          }}
        >
          <IconSystem.warning size={20} style={{ color: '#ef4444', flexShrink: 0, marginTop: '0.125rem' }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, color: '#dc2626', marginBottom: '0.25rem', fontSize: '0.875rem' }}>
              Some data failed to load
            </div>
            <ul style={{ margin: 0, paddingLeft: '1.25rem', color: '#b91c1c', fontSize: '0.8125rem' }}>
              {summaryError && <li>Dashboard Summary: {summaryError}</li>}
              {workSummaryError && <li>Work Summary: {workSummaryError}</li>}
              {typeSummaryError && <li>Request Types: {typeSummaryError}</li>}
              {statusSummaryError && <li>Status Summary: {statusSummaryError}</li>}
              {consolidationStatsError && <li>Consolidation Statistics: {consolidationStatsError}</li>}
              {consolidationError && <li>Consolidation Action: {consolidationError}</li>}
            </ul>
          </div>
          <button
            onClick={() => {
              if (summaryError) refetchSummary();
              if (workSummaryError) refetchWorkSummary();
              if (typeSummaryError) refetchTypeSummary();
              if (statusSummaryError) refetchStatusSummary();
              if (consolidationStatsError) refetchConsolidationStats();
            }}
            style={{
              padding: '0.375rem 0.75rem',
              fontSize: '0.75rem',
              fontWeight: 500,
              background: '#fee2e2',
              border: '1px solid #fca5a5',
              borderRadius: '0.375rem',
              color: '#dc2626',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            Retry
          </button>
        </div>
      )}

      {/* Statistics and Tabs - Before module-content */}
      <div className="das-content-wrapper">
        {/* Statistics Bar - Show skeleton when loading */}
        {isInitialLoading ? (
          <StatsSkeleton isDarkMode={isDarkMode} itemCount={5} />
        ) : (
        <div
          className="stats-container"
          style={{
            display: 'flex',
            gap: '0.5rem',
            margin: '0.5rem 1.5rem 0.5rem 1.5rem',
            padding: '0.375rem 0.75rem',
            borderRadius: '0.5rem',
            overflowX: 'auto',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            background: isDarkMode ? '#1e293b' : '#f8fafc',
            border: `1px solid ${isDarkMode ? '#475569' : '#e2e8f0'}`,
          }}
        >
          <div
            style={{
              textAlign: 'center',
              padding: '0.125rem',
              minWidth: '70px',
              flex: '1 1 auto',
            }}
          >
            <div
              style={{
                fontSize: '0.875rem',
                fontWeight: 700,
                color: '#3b82f6',
                lineHeight: 1.2,
              }}
            >
              {stats.myActiveItems}
            </div>
            <div
              style={{
                fontSize: '0.625rem',
                marginTop: '0.0625rem',
                whiteSpace: 'nowrap',
                color: isDarkMode ? '#e2e8f0' : '#64748b',
              }}
            >
              Active Items
            </div>
          </div>
          <div
            style={{
              textAlign: 'center',
              padding: '0.125rem',
              minWidth: '70px',
              flex: '1 1 auto',
            }}
          >
            <div
              style={{ fontSize: '0.875rem', fontWeight: 700, color: '#f59e0b', lineHeight: 1.2 }}
            >
              {stats.myPendingApprovals}
            </div>
            <div
              style={{
                fontSize: '0.625rem',
                marginTop: '0.0625rem',
                whiteSpace: 'nowrap',
                color: isDarkMode ? '#e2e8f0' : '#64748b',
              }}
            >
              Pending
            </div>
          </div>
          <div
            style={{
              textAlign: 'center',
              padding: '0.125rem',
              minWidth: '70px',
              flex: '1 1 auto',
            }}
          >
            <div
              style={{ fontSize: '0.875rem', fontWeight: 700, color: '#10b981', lineHeight: 1.2 }}
            >
              {stats.requestsCreatedByMe}
            </div>
            <div
              style={{
                fontSize: '0.625rem',
                marginTop: '0.0625rem',
                whiteSpace: 'nowrap',
                color: isDarkMode ? '#e2e8f0' : '#64748b',
              }}
            >
              My Requests
            </div>
          </div>
          <div
            style={{
              textAlign: 'center',
              padding: '0.125rem',
              minWidth: '70px',
              flex: '1 1 auto',
            }}
          >
            <div
              style={{ fontSize: '0.875rem', fontWeight: 700, color: '#ef4444', lineHeight: 1.2 }}
            >
              {stats.criticalTickets}
            </div>
            <div
              style={{
                fontSize: '0.625rem',
                marginTop: '0.0625rem',
                whiteSpace: 'nowrap',
                color: isDarkMode ? '#e2e8f0' : '#64748b',
              }}
            >
              Critical
            </div>
          </div>
          {/* Consolidation Stats - Two-Level Approval (User-Specific) */}
          {/* Pre-Approved: Only show for users who are pre-approvers (have pre-approved items) */}
          {userConsolidationCounts.hasPreApprovedRole && (
            <>
              <div
                style={{
                  width: '1px',
                  background: isDarkMode ? '#475569' : '#e2e8f0',
                  margin: '0.25rem 0.5rem',
                }}
              />
              <div
                style={{
                  textAlign: 'center',
                  padding: '0.125rem',
                  minWidth: '70px',
                  flex: '1 1 auto',
                }}
              >
                <div
                  style={{ fontSize: '0.875rem', fontWeight: 700, color: '#f59e0b', lineHeight: 1.2 }}
                >
                  {userConsolidationCounts.preApprovedCount}
                </div>
                <div
                  style={{
                    fontSize: '0.625rem',
                    marginTop: '0.0625rem',
                    whiteSpace: 'nowrap',
                    color: isDarkMode ? '#e2e8f0' : '#64748b',
                  }}
                >
                  Pre-Approved
                </div>
              </div>
            </>
          )}
          {/* Final Approval: Only show for users who are final approvers */}
          {userConsolidationCounts.hasFinalApproverRole && (
            <>
              {!userConsolidationCounts.hasPreApprovedRole && (
                <div
                  style={{
                    width: '1px',
                    background: isDarkMode ? '#475569' : '#e2e8f0',
                    margin: '0.25rem 0.5rem',
                  }}
                />
              )}
              <div
                style={{
                  textAlign: 'center',
                  padding: '0.125rem',
                  minWidth: '70px',
                  flex: '1 1 auto',
                }}
              >
                <div
                  style={{ fontSize: '0.875rem', fontWeight: 700, color: '#3b82f6', lineHeight: 1.2 }}
                >
                  {userConsolidationCounts.pendingFinalApprovalCount}
                </div>
                <div
                  style={{
                    fontSize: '0.625rem',
                    marginTop: '0.0625rem',
                    whiteSpace: 'nowrap',
                    color: isDarkMode ? '#e2e8f0' : '#64748b',
                  }}
                >
                  Final Approval
                </div>
              </div>
            </>
          )}
        </div>
        )}

        {/* Tabs - Responsive with Overflow Menu & Glassmorphism */}
        <div
          style={{
            display: 'flex',
            gap: '0.25rem',
            margin: '0 1.5rem',
            position: 'relative',
            overflow: 'visible',
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-light)',
            borderBottom: 'none',
            borderRadius: '0.5rem 0.5rem 0 0',
            padding: '0.25rem 0.5rem',
          }}
        >
          {/* Visible Tabs - Show first 3 on mobile, all on desktop */}
          <div
            style={{
              display: 'flex',
              gap: '0.25rem',
              flex: 1,
              overflowX: 'auto',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
            }}
            className="tab-container"
          >
            {TAB_CONFIGS.slice(0, 3).map((tab) => (
              <button
                key={tab.id}
                className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => handleTabChange(tab.id)}
                style={{
                  padding: '0.25rem 0.625rem',
                  color: activeTab === tab.id ? 'white' : 'var(--text-secondary)',
                  borderRadius: '0.375rem',
                  cursor: 'pointer',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                }}
              >
                {tab.label}
              </button>
            ))}

            {/* Desktop-only tabs (hidden on mobile) */}
            <div
              style={{
                display: 'flex',
                gap: '0.25rem',
              }}
              className="desktop-tabs"
            >
              {TAB_CONFIGS.slice(3).map((tab) => (
                <button
                  key={tab.id}
                  className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => handleTabChange(tab.id)}
                  style={{
                    padding: '0.25rem 0.625rem',
                    color: activeTab === tab.id ? 'white' : 'var(--text-secondary)',
                    borderRadius: '0.375rem',
                    cursor: 'pointer',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Overflow Menu Button - Mobile Only */}
          <div
            ref={tabOverflowRef}
            className="tab-overflow-menu action-menu-container"
          >
            <button
              onClick={() => setTabOverflowOpen(!tabOverflowOpen)}
              className={`action-menu-trigger ${tabOverflowOpen ? 'active' : ''}`}
              style={{
                borderRadius: '0.375rem',
                padding: '0.25rem 0.375rem',
              }}
            >
              <IconSystem.moreVertical size={16} />
            </button>

            {/* Overflow Dropdown */}
            {tabOverflowOpen && (
              <div
                className="action-menu-dropdown"
                style={{
                  minWidth: '200px',
                  maxHeight: '400px',
                  overflowY: 'auto',
                  padding: '0.25rem 0',
                }}
              >
                {TAB_CONFIGS.slice(3).map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => {
                      handleTabChange(tab.id);
                      setTabOverflowOpen(false);
                    }}
                    className="action-menu-item"
                    style={{
                      fontWeight: activeTab === tab.id ? 600 : 400,
                      background:
                        activeTab === tab.id ? 'var(--bg-secondary)' : 'transparent',
                      color:
                        activeTab === tab.id ? 'var(--primary)' : 'var(--text-primary)',
                    }}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Module Content */}
      <div className="module-content">
        <div className="data-grid-container" style={{ position: 'relative' }}>
          {/* Loading Overlay for Refresh Operations */}
          <LoadingOverlay
            isVisible={isRefreshing}
            message="Refreshing data..."
            isDarkMode={isDarkMode}
          />

          {/* LIST VIEW */}
          {viewMode === 'list' && (
            <>
              <div className="data-table-wrapper">
                <table className="data-table">
                  <thead>
                    <tr>
                      {visibleColumnDefs.map((col) => (
                        <th
                          key={col.id}
                          style={{ width: col.width }}
                          className={col.id === 'actions' ? 'action-column' : undefined}
                        >
                          {col.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {/* Show skeleton rows during initial loading */}
                    {isInitialLoading ? (
                      <TableSkeleton rowCount={8} columnCount={visibleColumnDefs.length} isDarkMode={isDarkMode} />
                    ) : filteredData.length === 0 ? (
                      <tr>
                        <td
                          colSpan={visibleColumnDefs.length}
                          style={{
                            textAlign: 'center',
                            padding: '2rem',
                            color: workSummaryError ? '#dc2626' : 'var(--text-muted)',
                          }}
                        >
                          {workSummaryError ? (
                            <>
                              <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Failed to load requests</div>
                              <div style={{ fontSize: '0.85rem' }}>{workSummaryError}</div>
                            </>
                          ) : !myWorkSummary ? (
                            'Unable to load work summary data'
                          ) : (
                            'No requests found'
                          )}
                        </td>
                      </tr>
                    ) : (
                      filteredData.map((ticket) => (
                        <tr
                          key={ticket.id}
                          className={activeMenuRowId === ticket.id ? 'has-active-menu' : ''}
                        >
                          {visibleColumnDefs.map((col) => {
                            if (col.id === 'ticketNo')
                              return (
                                <td key={col.id} style={{ textAlign: 'left' }}>
                                  <strong>{ticket.ticketNo}</strong>
                                </td>
                              );
                            if (col.id === 'subject') return <td key={col.id}>{ticket.subject}</td>;
                            if (col.id === 'requestType')
                              return <td key={col.id}>{ticket.requestType}</td>;
                            if (col.id === 'yourRole')
                              return <td key={col.id}>{ticket.yourRole}</td>;
                            if (col.id === 'currentStage') {
                              const stageColor = getStageHexColor(ticket.currentStage);
                              return (
                                <td key={col.id}>
                                  <span
                                    style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      padding: '0.25rem 0.625rem',
                                      fontSize: '0.75rem',
                                      fontWeight: 600,
                                      borderRadius: '0.25rem',
                                      background: `${stageColor}15`,
                                      color: stageColor,
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {ticket.currentStage}
                                  </span>
                                </td>
                              );
                            }
                            if (col.id === 'yourActionStatus')
                              return (
                                <td key={col.id}>
                                  <span
                                    className={`badge ${getActionStatusColor(
                                      ticket.yourActionStatus
                                    )}`}
                                  >
                                    {ticket.yourActionStatus}
                                  </span>
                                </td>
                              );
                            if (col.id === 'priority') {
                              const priorityColor = getPriorityHexColor(ticket.priority);
                              return (
                                <td key={col.id}>
                                  <span
                                    style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      padding: '0.25rem 0.625rem',
                                      fontSize: '0.75rem',
                                      fontWeight: 600,
                                      borderRadius: '0.25rem',
                                      background: `${priorityColor}15`,
                                      color: priorityColor,
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {ticket.priority}
                                  </span>
                                </td>
                              );
                            }
                            if (col.id === 'pendingSince')
                              return (
                                <td key={col.id}>
                                  {ticket.pendingSince} ({ticket.pendingAge} days)
                                </td>
                              );
                            if (col.id === 'comments')
                              return <td key={col.id}>{ticket.comments || '-'}</td>;
                            if (col.id === 'documentCount')
                              return <td key={col.id}>{ticket.documentCount || 0}</td>;
                            if (col.id === 'finalApprover')
                              return (
                                <td key={col.id}>
                                  {ticket.finalApproverName || <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>Not assigned</span>}
                                </td>
                              );
                            if (col.id === 'actions')
                              return (
                                <td key={col.id} className="action-column">
                                  <ActionMenu
                                    rowId={ticket.id as string}
                                    onView={handleView}
                                    onTakeAction={handleTakeAction}
                                    onMenuStateChange={handleMenuStateChange}
                                    yourActionStatus={ticket.yourActionStatus}
                                    currentStage={ticket.currentStage}
                                    yourRole={ticket.yourRole}
                                    onResubmit={handleResubmit}
                                    onCancel={handleCancel}
                                    returnedToUserId={ticket.returnedToUserId}
                                    currentUserId={currentUserId?.toString()}
                                  />
                                </td>
                              );
                            return null;
                          })}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination - server-side for "All Requests" tab, record count for other tabs */}
              {activeTab === 'all-tickets' && paginationData ? (
                <div className="pagination-footer">
                  <div className="pagination-info">
                    <span>
                      Showing{' '}
                      {paginationData.totalItems === 0
                        ? 0
                        : (paginationData.currentPage - 1) * paginationData.pageSize + 1}
                      -{Math.min(paginationData.currentPage * paginationData.pageSize, paginationData.totalItems)}{' '}
                      of {paginationData.totalItems} requests
                    </span>
                    <select
                      value={pageSize}
                      onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                      style={{
                        marginLeft: '1rem',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '4px',
                        border: '1px solid var(--border-light)',
                        background: 'var(--bg-primary)',
                        color: 'var(--text-primary)',
                        cursor: 'pointer',
                      }}
                    >
                      <option value={10}>10 per page</option>
                      <option value={20}>20 per page</option>
                      <option value={50}>50 per page</option>
                      <option value={100}>100 per page</option>
                    </select>
                  </div>
                  <div className="pagination-controls">
                    <button
                      className="page-btn"
                      onClick={handlePreviousPage}
                      disabled={!paginationData.hasPreviousPage}
                      title="Previous page"
                    >
                      <IconSystem.chevronLeft size={16} />
                    </button>

                    {/* Page Numbers */}
                    {Array.from({ length: paginationData.totalPages }, (_, i) => i + 1)
                      .filter((page) => {
                        // Show first, last, current, and adjacent pages
                        return (
                          page === 1 ||
                          page === paginationData.totalPages ||
                          Math.abs(page - paginationData.currentPage) <= 1
                        );
                      })
                      .map((page, index, array) => {
                        const prevPage = array[index - 1];
                        const showEllipsis = prevPage && page - prevPage > 1;

                        return (
                          <React.Fragment key={page}>
                            {showEllipsis && (
                              <span style={{ padding: '0.5rem', color: 'var(--text-muted)' }}>...</span>
                            )}
                            <button
                              className={`page-btn ${page === paginationData.currentPage ? 'active' : ''}`}
                              onClick={() => handlePageChange(page)}
                            >
                              {page}
                            </button>
                          </React.Fragment>
                        );
                      })}

                    <button
                      className="page-btn"
                      onClick={handleNextPage}
                      disabled={!paginationData.hasNextPage}
                      title="Next page"
                    >
                      <IconSystem.chevronRight size={16} />
                    </button>
                  </div>
                </div>
              ) : activeTab !== 'all-tickets' && filteredData.length > 0 ? (
                <div className="pagination-footer">
                  <div className="pagination-info">
                    <span>
                      Showing {filteredData.length} {filteredData.length === 1 ? 'request' : 'requests'}
                    </span>
                  </div>
                </div>
              ) : null}
            </>
          )}

          {/* GRID VIEW */}
          {viewMode === 'grid' && (
            isInitialLoading ? (
              <GridViewSkeleton cardCount={6} isDarkMode={isDarkMode} />
            ) : (
            <div
              style={{
                padding: '1rem',
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                gap: '1.5rem',
                overflowY: 'auto',
              }}
            >
              {filteredData.length === 0 ? (
                <div
                  style={{
                    gridColumn: '1 / -1',
                    textAlign: 'center',
                    padding: '3rem',
                    color: workSummaryError ? '#dc2626' : 'var(--text-muted)',
                  }}
                >
                  <IconSystem.grid size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
                  {workSummaryError ? (
                    <>
                      <p style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Failed to load requests</p>
                      <p style={{ fontSize: '0.85rem' }}>{workSummaryError}</p>
                    </>
                  ) : !myWorkSummary ? (
                    <p>Unable to load work summary data</p>
                  ) : (
                    <p>No requests found</p>
                  )}
                </div>
              ) : (
                filteredData.map((ticket) => (
                  <TicketCard
                    key={ticket.id}
                    ticket={ticket}
                    onView={handleView}
                    onTakeAction={handleTakeAction}
                  />
                ))
              )}
            </div>
            )
          )}

          {/* CALENDAR VIEW (DISABLED MESSAGE) */}
          {viewMode === 'calendar' && (
            <div
              style={{
                padding: '3rem',
                textAlign: 'center',
                color: 'var(--text-muted)',
              }}
            >
              <IconSystem.calendar size={48} style={{ marginBottom: '1rem', opacity: 0.3 }} />
              <h3 style={{ color: '#64748b', marginBottom: '0.5rem' }}>
                Calendar View Not Applicable
              </h3>
              <p>Calendar view is not available for Digital Approval System</p>
            </div>
          )}
        </div>

        {/* Filter Panel */}
        <div className={`search-panel ${filterOpen ? '' : 'collapsed'}`}>
          <div className="search-panel-header">
            <h3 className="search-panel-title">Filters</h3>
          </div>

          <div className="search-panel-content">
            {/* Request Type Filter - Dynamic from backend */}
            <div className="filter-section">
              <label className="filter-section-title">Request Type</label>
              <div className="form-group">
                <select
                  className="form-select"
                  value={requestTypeFilter}
                  onChange={(e) => setRequestTypeFilter(e.target.value as RequestType | '')}
                >
                  <option value="">All Types</option>
                  {typeSummary.map((type) => (
                    <option key={type.requestTypeId} value={type.requestTypeName}>
                      {type.requestTypeName} ({type.count})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Priority Filter - Uses backend-aligned values */}
            <div className="filter-section">
              <label className="filter-section-title">Priority</label>
              <div className="form-group">
                <select
                  className="form-select"
                  value={priorityFilter}
                  onChange={(e) => setPriorityFilter(e.target.value as TicketPriority | '')}
                >
                  <option value="">All Priorities</option>
                  {PRIORITY_OPTIONS.map((priority) => (
                    <option key={priority} value={priority}>
                      {priority}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Stage Filter - Dynamic from backend status summary */}
            <div className="filter-section">
              <label className="filter-section-title">Current Stage</label>
              <div className="form-group">
                <select
                  className="form-select"
                  value={stageFilter}
                  onChange={(e) => setStageFilter(e.target.value as TicketStage | '')}
                >
                  <option value="">All Stages</option>
                  {statusSummary.map((status) => (
                    <option key={status.statusCode} value={status.statusName}>
                      {status.statusName} ({status.count})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={handleClearFilters}>
              Clear
            </button>
            <button className="btn btn-primary" onClick={() => setFilterOpen(false)}>
              Apply
            </button>
          </div>
        </div>
      </div>

      <style>{`
        /* Align wrapper - no padding, children use margin for alignment */
        .das-content-wrapper {
          width: 100%;
          max-width: 100%;
          box-sizing: border-box;
          padding: 0;
          overflow: visible;
          position: relative;
          z-index: 10; /* Higher than module-content (1) to allow dropdowns to appear above grid */
        }

        /* Glassmorphism effect for stats container */
        .das-content-wrapper .stats-container {
          background: rgba(255, 255, 255, 0.7) !important;
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.5) !important;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05),
                      0 0 20px rgba(0, 0, 0, 0.02);
        }

        /* Tabs container styling removed - using inline styles now */

        /* Enhanced tab buttons with glassmorphism */
        .das-content-wrapper .tab-button {
          background: rgba(255, 255, 255, 0.3) !important;
          backdrop-filter: blur(5px);
          -webkit-backdrop-filter: blur(5px);
          border: 1px solid rgba(255, 255, 255, 0.3) !important;
          transition: all 0.3s ease !important;
        }

        .das-content-wrapper .tab-button:hover {
          background: rgba(255, 255, 255, 0.5) !important;
          transform: translateY(-1px);
        }

        .das-content-wrapper .tab-button.active {
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.9), rgba(37, 99, 235, 0.9)) !important;
          border: 1px solid rgba(255, 255, 255, 0.6) !important;
          box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
        }

        /* Tab container - lower z-index to allow overflow menu on top */
        .tab-container {
          position: relative;
          z-index: 1;
        }

        /* Tab buttons - prevent creating higher stacking contexts */
        .tab-container .tab-button {
          position: relative;
          z-index: 1;
        }

        /* Tab overflow menu - ensure it's always on top */
        .tab-overflow-menu {
          z-index: 10000 !important;
          position: relative !important;
          isolation: isolate;
        }

        /* Hide desktop tabs on mobile */
        @media (max-width: 768px) {
          .desktop-tabs {
            display: none !important;
          }
          .tab-overflow-menu {
            display: block;
          }
        }

        /* Hide overflow menu on desktop */
        @media (min-width: 769px) {
          .tab-overflow-menu {
            display: none;
          }
          .desktop-tabs {
            display: flex !important;
          }
        }

        /* Hide scrollbar but keep functionality */
        .tab-container::-webkit-scrollbar {
          display: none;
        }

        .stats-container::-webkit-scrollbar {
          display: none;
        }

        /* Make stats even more compact on very small screens */
        @media (max-width: 480px) {
          .stats-container > div {
            min-width: 80px !important;
          }
          .stats-container > div > div:first-child {
            font-size: 0.9375rem !important;
          }
          .stats-container > div > div:last-child {
            font-size: 0.5625rem !important;
          }
        }
      `}</style>

      {/* New Ticket Modal - onSave prop removed, modal now handles API calls internally */}
      <NewTicketModal
        isOpen={newTicketModalOpen}
        onClose={() => setNewTicketModalOpen(false)}
        onSuccess={() => {
          // Refetch dashboard data after successful creation
          refetchSummary();
          refetchWorkSummary();
          console.log('🔄 Refetching dashboard data after successful creation');
        }}
        predefinedWorkflows={workflowTemplates.map((template) => ({
          id: template.id as string,
          name: template.workflowTemplateName,
          description: template.description || `${template.steps.length} levels`,
          category: template.category || 'General',
          steps: template.steps.map((step) => ({
            level: step.level,
            approverName: step.approverRoleName || step.approverUserName || 'Approver',
            approverEmail: step.approverEmail || '',
            role: step.approverRoleName || 'Approver',
          })),
        }))}
      />

      {/* Take Action Modal */}
      {showTakeActionModal && (
        <>
          {takeActionLoading && (
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10050,
              }}
            >
              <div
                style={{
                  background: 'white',
                  padding: '2rem',
                  borderRadius: '0.5rem',
                  boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
                }}
              >
                <p style={{ margin: 0, fontSize: '1rem', color: '#1e293b' }}>Loading request data...</p>
              </div>
            </div>
          )}

          {takeActionError && (
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10050,
              }}
            >
              <div
                style={{
                  background: 'white',
                  padding: '2rem',
                  borderRadius: '0.5rem',
                  boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
                  maxWidth: '500px',
                }}
              >
                <h3 style={{ margin: '0 0 1rem 0', color: '#dc2626' }}>Error Loading Request</h3>
                <p style={{ margin: '0 0 1.5rem 0', color: '#64748b' }}>{takeActionError}</p>
                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => {
                      setShowTakeActionModal(false);
                      setSelectedTicketId(null);
                    }}
                    style={{
                      padding: '0.5rem 1rem',
                      background: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.375rem',
                      cursor: 'pointer',
                      fontWeight: 500,
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}

          {ticket && approvalPosition && ticket.id === selectedTicketId && (
            <TakeActionModal
              ticket={ticket}
              currentUserPosition={approvalPosition}
              relatedTickets={relatedTickets}
              approvalLevels={approvalLevels}
              availableUsers={availableUsers}
              onClose={() => {
                setShowTakeActionModal(false);
                setSelectedTicketId(null);
              }}
              onActionTaken={async (request) => {
                try {
                  await takeAction(request);
                  alert(`Action "${request.action}" submitted successfully!`);
                  setShowTakeActionModal(false);
                  setSelectedTicketId(null);
                  // Refresh all dashboard data after action
                  console.log('🔄 Refreshing dashboard after action...');
                  refetchSummary();
                  refetchWorkSummary();
                  // Also refetch request details for when user views the ticket again
                  refetchRequestDetails();
                } catch (error) {
                  console.error('Failed to submit action:', error);
                  throw error; // Re-throw to let modal handle error display
                }
              }}
            />
          )}
        </>
      )}

      {/* Ticket Details Modal */}
      {showDetailsModal && (
        <>
          {/* Loading State */}
          {requestDetailsLoading && !requestDetailsError && (
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10050,
              }}
            >
              <div
                style={{
                  background: 'white',
                  padding: '2rem',
                  borderRadius: '0.5rem',
                  boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
                }}
              >
                <p style={{ margin: 0, fontSize: '1rem', color: '#1e293b' }}>Loading request details...</p>
              </div>
            </div>
          )}

          {/* Error State */}
          {requestDetailsError && (
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10050,
              }}
            >
              <div
                style={{
                  background: 'white',
                  padding: '2rem',
                  borderRadius: '0.5rem',
                  boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
                  maxWidth: '500px',
                }}
              >
                <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.25rem', color: '#dc2626' }}>
                  Error Loading Ticket
                </h3>
                <p style={{ margin: '0 0 1.5rem 0', fontSize: '1rem', color: '#64748b' }}>
                  {requestDetailsError.message}
                </p>
                <button
                  onClick={() => {
                    setShowDetailsModal(false);
                    setSelectedTicketId(null);
                  }}
                  style={{
                    background: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    padding: '0.5rem 1rem',
                    borderRadius: '0.375rem',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          )}

          {/* Actual Modal */}
          {!requestDetailsLoading && !requestDetailsError && ticketDetails && ticketDetails.id === selectedTicketId && (
            <TicketModalRouter
              isOpen={showDetailsModal}
              onClose={() => {
                setShowDetailsModal(false);
                setSelectedTicketId(null);
              }}
              ticket={ticketDetails}
              onTakeAction={() => {
                setShowDetailsModal(false);
                setShowTakeActionModal(true);
              }}
              onResubmit={() => {
                setShowDetailsModal(false);
                setShowResubmitModal(true);
              }}
              // Only show Cancel option to the original creator of the request
              onCancel={ticketDetails.yourRole === 'Creator' ? () => {
                setShowDetailsModal(false);
                setShowCancelModal(true);
              } : undefined}
            />
          )}
        </>
      )}

      {/* Resubmit Request Modal */}
      {showResubmitModal && ticketDetails && (
        <ResubmitRequestModal
          isOpen={showResubmitModal}
          onClose={() => {
            setShowResubmitModal(false);
            setSelectedTicketId(null);
          }}
          ticket={ticketDetails}
          onSuccess={() => {
            // Refresh the data after successful resubmit
            refetchSummary();
            refetchWorkSummary();
            refetchRequestDetails();
          }}
        />
      )}

      {/* Cancel Request Modal */}
      {showCancelModal && ticketDetails && (
        <CancelRequestModal
          isOpen={showCancelModal}
          onClose={() => {
            setShowCancelModal(false);
            setSelectedTicketId(null);
          }}
          ticket={ticketDetails}
          onSuccess={() => {
            // Refresh the data after successful cancel
            refetchSummary();
            refetchWorkSummary();
            refetchRequestDetails();
          }}
        />
      )}

      {/* New Consolidated Request Modal */}
      {showNewConsolidateModal && (
        <ConsolidatedRequestModal
          isOpen={showNewConsolidateModal}
          onClose={() => setShowNewConsolidateModal(false)}
          onSubmit={async (formData, selectedTickets) => {
            // Build original approvers list from selected tickets
            const originalApproversMap = new Map<string, { name: string; type: 'user' | 'role'; count: number }>();
            selectedTickets.forEach((ticket) => {
              const approver = ticket.finalApprover;
              if (originalApproversMap.has(approver.id as string)) {
                const existing = originalApproversMap.get(approver.id as string)!;
                existing.count += 1;
              } else {
                originalApproversMap.set(approver.id as string, {
                  name: approver.name,
                  type: approver.type,
                  count: 1,
                });
              }
            });

            // Create consolidated request using existing API
            const request: CreateConsolidatedRequestRequest = {
              title: formData.title,
              description: formData.description || undefined,
              preApprovedRequestIds: formData.selectedTicketIds as string[],
              priority: formData.priority,
              // Set final approver based on type (user or role)
              finalApproverUserId: formData.finalApproverType === 'user'
                ? formData.finalApproverId as string
                : undefined,
              finalApproverRoleId: formData.finalApproverType === 'role'
                ? formData.finalApproverId as string
                : undefined,
              // Include final approver name for workflow history display
              finalApproverName: formData.finalApproverName,
              // Approver change tracking
              isApproverChanged: formData.isApproverChanged || false,
              approverChangeReason: formData.approverChangeReason || undefined,
              originalApprovers: formData.isApproverChanged
                ? Array.from(originalApproversMap.entries()).map(([id, info]) => ({
                    approverId: id,
                    approverName: info.name,
                    approverType: info.type,
                    ticketCount: info.count,
                  }))
                : undefined,
            };

            try {
              const result = await createConsolidation(request);
              console.log('Consolidation created:', result);

              // Upload attachments if any
              const consolidatedRequestId = result?.consolidatedRequest?.id;
              if (consolidatedRequestId && formData.attachments.length > 0) {
                console.log('Uploading attachments:', formData.attachments.length);

                // Upload each attachment
                const uploadPromises = formData.attachments.map(async (attachment) => {
                  try {
                    await attachmentsService.upload(attachment.file, {
                      requestId: consolidatedRequestId,
                      documentTitle: attachment.title || attachment.file.name,
                      documentDescription: attachment.description,
                    });
                    console.log('Uploaded attachment:', attachment.file.name);
                  } catch (uploadError) {
                    console.error('Failed to upload attachment:', attachment.file.name, uploadError);
                    // Continue with other uploads even if one fails
                  }
                });

                // Wait for all uploads to complete
                await Promise.allSettled(uploadPromises);
                console.log('All attachments processed');
              }

              // Refresh data and close modal
              refetchSummary();
              refetchWorkSummary();
              if (refetchConsolidationStats) refetchConsolidationStats();
              setSelectedPreApprovedIds([]);
              setShowNewConsolidateModal(false);
            } catch (error) {
              console.error('Failed to create consolidation:', error);
              throw error;
            }
          }}
          preApprovedTickets={allTickets
            .filter((t) => t.currentStage === 'Pre Approved')
            .map((ticket): PreApprovedTicket => ({
              id: ticket.id,
              ticketNo: ticket.ticketNo,
              subject: ticket.subject,
              description: ticket.comments,
              requestType: ticket.requestType as any,
              value: ticket.value || 0,
              priority: ticket.priority as any,
              createdBy: {
                id: ticket.id, // Using ticket id as placeholder
                name: ticket.createdByUserName || 'Unknown',
                department: undefined,
              },
              createdDate: ticket.pendingSince,
              preApprovedBy: {
                id: ticket.id, // Using ticket id as placeholder
                name: ticket.lastActionBy || 'Unknown',
                role: 'Pre-Approver',
              },
              preApprovedDate: ticket.pendingSince,
              preApprovalComment: undefined,
              finalApprover: {
                id: ticket.finalApproverUserId || '',
                name: ticket.finalApproverName || 'Not Assigned',
                role: 'Final Approver',
                type: 'user',
              },
              attachmentCount: ticket.documentCount || 0,
            }))}
          currentUserId={currentUserId || ''}
          currentUserName={authUser?.fullName || authUser?.firstName || 'Unknown User'}
        />
      )}

      {/* Consolidate Pre-Approved Items Modal - Enhanced (Legacy) */}
      {showConsolidateModal && modalSelectedIds.length > 0 && (() => {
        // Get selected tickets data based on modalSelectedIds
        const selectedTickets = filteredData.filter((t) => modalSelectedIds.includes(t.id as string));
        const totalDocuments = selectedTickets.reduce((sum, t) => sum + (t.documentCount || 0), 0);
        const totalValue = selectedTickets.reduce((sum, t) => sum + (t.value || 0), 0);

        // Format value without currency symbol
        const formatValue = (value: number) => {
          return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
          }).format(value);
        };

        // Format file size
        const formatFileSize = (bytes: number) => {
          if (bytes === 0) return '0 Bytes';
          const k = 1024;
          const sizes = ['Bytes', 'KB', 'MB', 'GB'];
          const i = Math.floor(Math.log(bytes) / Math.log(k));
          return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
        };

        // Remove item from modal selection
        const handleRemoveModalItem = (ticketId: string) => {
          setModalSelectedIds((prev) => prev.filter((id) => id !== ticketId));
        };

        // Handle file selection
        const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
          const file = e.target.files?.[0];
          if (file) {
            setNewAttachment((prev) => ({ ...prev, file }));
          }
        };

        // Add attachment with title and description
        const handleAddAttachment = () => {
          if (!newAttachment.file) {
            return;
          }
          if (!newAttachment.title.trim()) {
            return;
          }
          setConsolidationAttachments((prev) => [
            ...prev,
            {
              file: newAttachment.file!,
              title: newAttachment.title,
              description: newAttachment.description,
            },
          ]);
          setNewAttachment({ file: null, title: '', description: '' });
          // Reset file input
          const fileInput = document.getElementById('consolidation-file-upload') as HTMLInputElement;
          if (fileInput) fileInput.value = '';
        };

        // Remove attachment
        const handleRemoveAttachment = (index: number) => {
          setConsolidationAttachments((prev) => prev.filter((_, i) => i !== index));
        };

        // Preview attachment in modal overlay
        const handlePreviewAttachment = (file: File) => {
          const url = URL.createObjectURL(file);
          setPreviewAttachment({
            url,
            fileName: file.name,
            fileType: file.type,
          });
        };

        // Close preview and cleanup blob URL
        const closePreview = () => {
          if (previewAttachment?.url) {
            URL.revokeObjectURL(previewAttachment.url);
          }
          setPreviewAttachment(null);
        };

        return (
          <>
            <div className="modal-backdrop" />
            <div className="modal-container" style={{ maxWidth: '1000px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              {/* Header */}
              <div className="modal-header" style={{ padding: '0.5rem 1rem', minHeight: 'auto' }}>
                <h2 className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '1rem' }}>
                  <IconSystem.copy size={18} style={{ color: 'var(--primary, #3b82f6)' }} />
                  Create Consolidated Request
                </h2>
                <button className="modal-close" onClick={() => setShowConsolidateModal(false)} style={{ width: '1.5rem', height: '1.5rem' }}>
                  <IconSystem.x size={16} />
                </button>
              </div>

              {/* Scrollable Content */}
              <div className="modal-content" style={{ overflowX: 'hidden' }}>
                {/* Selected Requests Section */}
                <div style={{ marginBottom: '20px' }}>
                  <h3 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600, color: 'var(--text-primary, #374151)' }}>
                    Selected Requests ({selectedTickets.length})
                  </h3>
                  <div
                    style={{
                      border: '2px solid var(--border-color, #cbd5e1)',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      maxHeight: '220px',
                      overflowY: 'auto',
                    }}
                  >
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', tableLayout: 'fixed' }}>
                      <thead>
                        <tr style={{ background: 'var(--bg-secondary, #f1f5f9)' }}>
                          <th style={{ padding: '10px 8px', textAlign: 'left', fontWeight: 600, borderBottom: '2px solid var(--border-color, #cbd5e1)', color: '#475569', width: '14%' }}>Request No</th>
                          <th style={{ padding: '10px 8px', textAlign: 'left', fontWeight: 600, borderBottom: '2px solid var(--border-color, #cbd5e1)', color: '#475569', width: '14%' }}>Request Type</th>
                          <th style={{ padding: '10px 8px', textAlign: 'left', fontWeight: 600, borderBottom: '2px solid var(--border-color, #cbd5e1)', color: '#475569', width: '14%' }}>Creator</th>
                          <th style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 600, borderBottom: '2px solid var(--border-color, #cbd5e1)', color: '#475569', width: '10%' }}>Value</th>
                          <th style={{ padding: '10px 8px', textAlign: 'left', fontWeight: 600, borderBottom: '2px solid var(--border-color, #cbd5e1)', color: '#475569', width: '16%' }}>Preapproved By</th>
                          <th style={{ padding: '10px 8px', textAlign: 'left', fontWeight: 600, borderBottom: '2px solid var(--border-color, #cbd5e1)', color: '#475569', width: '16%' }}>Final Approver</th>
                          <th style={{ padding: '10px 8px', textAlign: 'center', fontWeight: 600, borderBottom: '2px solid var(--border-color, #cbd5e1)', color: '#475569', width: '16%' }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {modalSelectedIds.map((ticketId, index) => {
                          const ticket = filteredData.find((t) => t.id === ticketId);
                          if (!ticket) return null;
                          return (
                            <tr
                              key={ticket.id}
                              style={{
                                background: index % 2 === 0 ? 'transparent' : 'var(--bg-secondary, #f9fafb)',
                                borderBottom: '1px solid var(--border-color, #e5e7eb)',
                              }}
                            >
                              <td style={{ padding: '10px 8px', fontWeight: 500, color: '#3b82f6' }}>
                                {ticket.ticketNo}
                              </td>
                              <td style={{ padding: '10px 8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {ticket.requestType}
                              </td>
                              <td style={{ padding: '10px 8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {ticket.createdByUserName || <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>N/A</span>}
                              </td>
                              <td style={{ padding: '10px 8px', fontWeight: 600, textAlign: 'right' }}>
                                {formatValue(ticket.value || 0)}
                              </td>
                              <td style={{ padding: '10px 8px', color: 'var(--text-secondary, #6b7280)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {ticket.lastActionBy || <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>N/A</span>}
                              </td>
                              <td style={{ padding: '10px 8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {ticket.finalApproverName || <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>Not assigned</span>}
                              </td>
                              <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                                <button
                                  onClick={() => handleRemoveModalItem(ticket.id as string)}
                                  disabled={modalSelectedIds.length <= 1}
                                  title={modalSelectedIds.length <= 1 ? 'At least one request is required' : 'Remove from consolidation'}
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: modalSelectedIds.length <= 1 ? 'not-allowed' : 'pointer',
                                    color: modalSelectedIds.length <= 1 ? '#d1d5db' : '#ef4444',
                                    padding: '4px',
                                  }}
                                >
                                  <IconSystem.trash size={16} />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Summary Cards */}
                <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
                  <div
                    style={{
                      flex: 1,
                      background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                      borderRadius: '6px',
                      padding: '6px 12px',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <span style={{ fontSize: '11px', opacity: 0.9 }}>Total Requests</span>
                    <span style={{ fontSize: '14px', fontWeight: 700 }}>{selectedTickets.length}</span>
                  </div>
                  <div
                    style={{
                      flex: 1,
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      borderRadius: '6px',
                      padding: '6px 12px',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <span style={{ fontSize: '11px', opacity: 0.9 }}>Total Value</span>
                    <span style={{ fontSize: '14px', fontWeight: 700 }}>{formatValue(totalValue)}</span>
                  </div>
                  <div
                    style={{
                      flex: 1,
                      background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                      borderRadius: '6px',
                      padding: '6px 12px',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <span style={{ fontSize: '11px', opacity: 0.9 }}>Attachments</span>
                    <span style={{ fontSize: '14px', fontWeight: 700 }}>{totalDocuments}</span>
                  </div>
                </div>

                {/* Title Field */}
                <div style={{ marginBottom: '16px' }}>
                  <label
                    htmlFor="consolidation-title"
                    style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: 600,
                      marginBottom: '6px',
                      color: 'var(--text-primary, #374151)',
                    }}
                  >
                    Consolidated Title <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    id="consolidation-title"
                    type="text"
                    value={consolidationTitle}
                    onChange={(e) => setConsolidationTitle(e.target.value)}
                    placeholder="Enter consolidated request title"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #334155',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>

                {/* Description Field */}
                <div style={{ marginBottom: '16px' }}>
                  <label
                    htmlFor="consolidation-description"
                    style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: 600,
                      marginBottom: '6px',
                      color: 'var(--text-primary, #374151)',
                    }}
                  >
                    Description / Notes for Final Approver
                  </label>
                  <textarea
                    id="consolidation-description"
                    value={consolidationDescription}
                    onChange={(e) => setConsolidationDescription(e.target.value)}
                    placeholder="Enter description or notes for the final approver..."
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #334155',
                      borderRadius: '6px',
                      fontSize: '14px',
                      resize: 'vertical',
                      fontFamily: 'inherit',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>

                {/* Additional Attachments Section - Following NewTicketModal Pattern */}
                <div style={{ marginBottom: '16px' }}>
                  <label
                    style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: 600,
                      marginBottom: '6px',
                      color: 'var(--text-primary, #374151)',
                    }}
                  >
                    Additional Attachments
                    <span style={{ fontSize: '0.8rem', fontWeight: 400, color: '#64748b', marginLeft: '0.5rem' }}>
                      (Optional)
                    </span>
                  </label>

                  {/* Attachment Input Form */}
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 2fr) minmax(0, 2fr) 50px',
                      gap: '8px',
                      marginBottom: '12px',
                    }}
                  >
                    <input
                      id="consolidation-file-upload"
                      type="file"
                      onChange={handleFileSelect}
                      style={{ fontSize: '0.875rem', padding: '8px', minWidth: 0, boxSizing: 'border-box' }}
                    />
                    <input
                      type="text"
                      placeholder="Title *"
                      value={newAttachment.title}
                      onChange={(e) => setNewAttachment((prev) => ({ ...prev, title: e.target.value }))}
                      style={{
                        padding: '8px 12px',
                        border: '1px solid var(--border-color, #d1d5db)',
                        borderRadius: '6px',
                        fontSize: '0.875rem',
                        minWidth: 0,
                        boxSizing: 'border-box',
                      }}
                    />
                    <input
                      type="text"
                      placeholder="Description"
                      value={newAttachment.description}
                      onChange={(e) => setNewAttachment((prev) => ({ ...prev, description: e.target.value }))}
                      style={{
                        padding: '8px 12px',
                        border: '1px solid var(--border-color, #d1d5db)',
                        borderRadius: '6px',
                        fontSize: '0.875rem',
                        minWidth: 0,
                        boxSizing: 'border-box',
                      }}
                    />
                    <button
                      type="button"
                      onClick={handleAddAttachment}
                      disabled={!newAttachment.file || !newAttachment.title.trim()}
                      title="Add attachment"
                      className={`btn ${(!newAttachment.file || !newAttachment.title.trim()) ? 'btn-disabled' : 'btn-primary'}`}
                      style={{
                        padding: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: 0,
                      }}
                    >
                      <IconSystem.add size={16} />
                    </button>
                  </div>

                  {/* Attachments List */}
                  {consolidationAttachments.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {consolidationAttachments.map((attachment, index) => (
                        <div
                          key={index}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '10px 12px',
                            background: '#f8fafc',
                            borderRadius: '6px',
                            border: '1px solid #e2e8f0',
                            minWidth: 0,
                          }}
                        >
                          <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                              <IconSystem.file size={14} style={{ color: '#64748b', flexShrink: 0 }} />
                              <span style={{ fontWeight: 500, fontSize: '0.875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={attachment.title}>{attachment.title}</span>
                            </div>
                            <div
                              style={{
                                fontSize: '0.75rem',
                                color: '#64748b',
                                marginLeft: '22px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                              title={`${attachment.file.name} (${formatFileSize(attachment.file.size)})${attachment.description ? ` • ${attachment.description}` : ''}`}
                            >
                              {attachment.file.name} ({formatFileSize(attachment.file.size)})
                              {attachment.description && ` • ${attachment.description}`}
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: '4px', flexShrink: 0, marginLeft: '8px' }}>
                            <button
                              type="button"
                              onClick={() => handlePreviewAttachment(attachment.file)}
                              title="Preview attachment"
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                color: '#3b82f6',
                                padding: '4px',
                              }}
                            >
                              <IconSystem.eye size={16} />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleRemoveAttachment(index)}
                              title="Remove attachment"
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                color: '#ef4444',
                                padding: '4px',
                              }}
                            >
                              <IconSystem.trash size={16} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div
                      style={{
                        padding: '16px',
                        background: '#f8fafc',
                        borderRadius: '6px',
                        border: '1px dashed #cbd5e1',
                        color: '#64748b',
                        fontSize: '0.875rem',
                        textAlign: 'center',
                      }}
                    >
                      No attachments added
                    </div>
                  )}
                </div>

                {/* Error message */}
                {consolidationError && (
                  <div
                    style={{
                      background: '#fee2e2',
                      border: '1px solid #fecaca',
                      borderRadius: '6px',
                      padding: '12px',
                      color: '#991b1b',
                      fontSize: '14px',
                    }}
                  >
                    {consolidationError}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="modal-footer" style={{ padding: '0.5rem 1rem', minHeight: 'auto', gap: '0.5rem' }}>
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowConsolidateModal(false);
                    setConsolidationTitle('');
                    setConsolidationDescription('');
                    setModalSelectedIds([]);
                    setConsolidationAttachments([]);
                    setNewAttachment({ file: null, title: '', description: '' });
                  }}
                  style={{ minWidth: '90px', height: '2rem', fontSize: '0.875rem' }}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleCreateConsolidation}
                  disabled={!consolidationTitle.trim() || modalSelectedIds.length === 0 || consolidationLoading}
                  style={{ minWidth: '90px', height: '2rem', fontSize: '0.875rem' }}
                >
                  {consolidationLoading ? 'Creating...' : 'Submit for Final Approval'}
                </button>
              </div>
            </div>

            {/* Attachment Preview Modal */}
            {previewAttachment && (
              <div
                onClick={closePreview}
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(0, 0, 0, 0.9)',
                  display: 'flex',
                  flexDirection: 'column',
                  zIndex: 11000,
                  padding: '2rem',
                }}
              >
                {/* Preview Header */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '1rem',
                    color: 'white',
                  }}
                >
                  <div>
                    <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>Attachment Preview</h3>
                    <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.875rem', opacity: 0.8 }}>
                      {previewAttachment.fileName}
                    </p>
                  </div>
                  <button
                    onClick={closePreview}
                    style={{
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      color: 'white',
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'background 0.2s',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)')}
                    title="Close preview"
                  >
                    <IconSystem.x size={24} />
                  </button>
                </div>

                {/* Preview Content */}
                <div
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    flex: 1,
                    background: 'white',
                    borderRadius: '0.5rem',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {previewAttachment.fileType.startsWith('image/') ? (
                    <img
                      src={previewAttachment.url}
                      alt={previewAttachment.fileName}
                      style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                    />
                  ) : previewAttachment.fileType.includes('pdf') ? (
                    <iframe
                      src={previewAttachment.url}
                      title={previewAttachment.fileName}
                      style={{ width: '100%', height: '100%', border: 'none' }}
                    />
                  ) : (
                    <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                      <IconSystem.file size={64} style={{ marginBottom: '1rem', opacity: 0.5 }} />
                      <p style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>Preview not available for this file type</p>
                      <p style={{ fontSize: '0.875rem', opacity: 0.7 }}>{previewAttachment.fileName}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        );
      })()}

    </div>
  );
};

export default DashboardScreen;
